import { LEGACY_PRODUCT_FLAGS } from '../config/productFeatureCatalog';
import { resolveZooProductScope as resolveSharedProductScope } from '../config/productAccessPolicy';
export { getProductConfiguration, normalizeProductConfiguration } from '../config/productAccessPolicy';

const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
/** Staff intent only (preset, programs, additions): the stored enabled list is recomputed at save time. */
export const productConfigurationIntent = (configuration) => configuration
  ? { version: configuration.version, tier: configuration.tier, programs: configuration.programs, additions: configuration.additions } : null;
const legacyTierValue = (value) => typeof value === 'string' ? value : typeof value?.value === 'string' ? value.value : '';
/** Historical commercial tier of a park without composition ('basic' maps to 'essential' as in the legacy readers). */
export function resolveLegacyProductTier(zoo) {
  const tier = (legacyTierValue(zoo?.followUpLevel) || legacyTierValue(zoo?.zooTiers)).trim().toLowerCase();
  if (!tier) return null;
  return tier === 'basic' ? 'essential' : tier;
}
/** Historical commercial tiers, stored as the V1 select option `{ value, label }` that legacy readers and the mobile app expect. */
export const LEGACY_PRODUCT_TIERS = Object.freeze(['discovery', 'essential', 'progress', 'fullcare', 'research_partner']);
export const LEGACY_PRODUCT_TIER_LABEL_KEYS = Object.freeze({
  discovery: 'discovery', essential: 'essential', progress: 'progress', fullcare: 'fullcare', research_partner: 'researchPartner',
});
const LEGACY_PRODUCT_TIER_LABELS = Object.freeze({
  discovery: '🔍 Discovery', essential: '📈 EBEA Essential', progress: '⭐ EBEA Progress',
  fullcare: '🚀 EBEA FullCare', research_partner: '🔬 EBEA Research Partner',
});
export const legacyTierOption = (value) => ({ value, label: LEGACY_PRODUCT_TIER_LABELS[value] || value });
/** Raw stored tier (no alias mapping): what the editor shows and what a concurrent edit is compared against. */
export const readLegacyTier = (zoo) => (legacyTierValue(zoo?.zooTiers) || legacyTierValue(zoo?.followUpLevel)).trim() || null;
const zooTypeValue = (zooType) => (zooType && typeof zooType === 'object' ? zooType.value : zooType);
/** ZIMS / Species360 n'existent pas pour un laboratoire (issue #1096) : import, synchro et collection ZIMS restent fermés. */
export const isLabEstablishment = (zoo) => zooTypeValue(zoo?.zooType) === 'lab';
const LAB_EXCLUDED_FEATURE = /^zims\./;
/** Web scope: the shared policy plus the legacy tier, needed by capabilities that never existed before the catalogue. */
export function resolveZooProductScope(zoo, userId) {
  const scope = resolveSharedProductScope(zoo, userId);
  const legacyTier = scope.mode === 'legacy' ? resolveLegacyProductTier(zoo) : null;
  if (!isLabEstablishment(zoo)) return { ...scope, legacyTier };
  const can = (id) => !LAB_EXCLUDED_FEATURE.test(id) && scope.can(id);
  // `excludes` : ce que ce type d'établissement n'a dans aucune offre (« Bien démarrer » ne le propose pas).
  const excludes = (id) => LAB_EXCLUDED_FEATURE.test(id);
  return { ...scope, legacyTier, can, excludes, allows: (ids) => !ids?.length || ids.some(can) };
}
export function projectLegacyFlags(configuration, currentFlags = {}) {
  const access = resolveSharedProductScope({ productConfiguration: configuration });
  return { ...currentFlags, ...Object.fromEntries(LEGACY_PRODUCT_FLAGS.map(({ id, legacyFlag }) => [legacyFlag, access.can(id)])) };
}

export function restoreLegacyFlags(configuration, currentFlags = {}) {
  const restored = { ...currentFlags };
  LEGACY_PRODUCT_FLAGS.forEach(({ legacyFlag }) => {
    if (own(configuration.legacyFeatureFlags || {}, legacyFlag)) restored[legacyFlag] = configuration.legacyFeatureFlags[legacyFlag];
    else delete restored[legacyFlag];
  });
  return restored;
}

export const PRODUCT_MENU_FEATURES = Object.freeze({
  DASHBOARD: [], MY_REMINDERS: [], HELP_CENTER: [], USER_SETTINGS: [], ZOO_MANAGEMENT: [],
  LIVE_VIEW: ['logbook.entries'], TIMELINE: [],
  VETERINARY_DASHBOARD: ['logbook.treatments', 'logbook.pharmacy', 'logbook.assistant'],
  TECHNICAL_DASHBOARD: ['extensions.technical'],
  TASK_MANAGEMENT: ['logbook.tasks', 'logbook.recurringTasks'], ANOMALY_ALERTS: ['logbook.anomalyAlerts'],
  OBJECTIVES_MANAGEMENT: ['welfare.goals'],
  GAMIFICATION: ['extensions.engagement'],
  SECTOR_MANAGEMENT: ['workspace.sectors'], TOURNEE_MANAGEMENT: ['logbook.rounds'],
  DAILY_REPORTS: ['logbook.dailyReports', 'logbook.templates', 'logbook.assistant'], ENCLOSURE_MANAGEMENT: ['workspace.enclosures'],
  ANIMAL_MANAGEMENT: ['workspace.animals'], GROUP_MANAGEMENT: ['workspace.groups'], GROUP_MANAGEMENT_OLD: ['workspace.groups'],
  ENRICHMENT_MANAGEMENT: ['monitoring.enrichment'],
  CUSTOM_PARAMETERS_HUB: ['monitoring.individualParameters', 'monitoring.environmentalParameters'],
  TRAINING_EXERCISES: ['monitoring.training'], TRAINING_DASHBOARD: ['monitoring.training'],
  ZIMS_COLLECTION: ['zims.syncIn', 'zims.import'], WEATHER: ['extensions.weather'],
  FOCAL_SAMPLING: ['observations.focalAnalysis'], SCAN_ANALYSIS: ['observations.scanAnalysis'],
  QUIZZ_ANALYSIS: ['assessments.species'], ENRICHMENT_ANALYSIS: ['monitoring.enrichmentAnalysis'],
  MULTI_PARAMETER_ANALYSIS: ['monitoring.crossAnalysis'], WEIGHT_ANALYSIS: ['monitoring.individualParameters'],
  CAMPAIGN_MANAGEMENT: ['observations.campaigns'], HISTORY: ['assessments.history'],
  FAST_EVALUATION: ['assessments.rapid', 'assessments.species', 'assessments.customGrids', 'assessments.individual'], INDIVIDUAL_FAST_EVALUATION: ['assessments.individual'],
  EVALUATION_MONITORING: ['assessments.species', 'observations.focal', 'observations.scan'], BILAN: ['assessments.reports'],
  QUIZZ_MONITORING: ['assessments.species'], ETHO_MONITORING: ['observations.ethograms'],
  // OBS_TRAINING n'est volontairement pas listé : participer à l'entraînement des
  // observateurs est ouvert à tout compte connecté ; `observations.training` ne règle
  // plus que le droit de lancer une séance (canLaunchObsTrainingSession, contrat v2 G).
  ENRICHMENT_GUIDES: ['extensions.enrichmentGuides'],
  GRID_MANAGEMENT: ['assessments.gridAuthoring'], SUPPORT_TICKETS: ['workspace.support'],
  USER_CREATION: ['workspace.users'], REPORT_MANAGEMENT: ['extensions.advancedReports'],
  SCIENTIFIC_PROJECTS: ['extensions.researchProjects'],
});

export const TIER_PRODUCT_FEATURES = Object.freeze({
  'enrichment.analysis': 'monitoring.enrichmentAnalysis',
  environmentalMonitoring: 'monitoring.environmentalParameters', scanSampling: 'observations.scan',
  veterinary: 'logbook.treatments', rafikiAI: 'extensions.rafiki',
  'sectorNotebook.advanced': 'logbook.entries', taskAutomation: 'logbook.recurringTasks',
  'campaign.scanSampling': 'observations.scan', 'campaign.environmental': 'monitoring.environmentalParameters',
  'campaign.focalSampling': 'observations.focal', akongoVision: 'observations.training',
});

export const PRODUCT_ACTIVITY_FEATURES = Object.freeze({
  object: 'monitoring.enrichment', note: 'logbook.entries', movement: 'workspace.animals',
  obs: 'observations.focal', observations: 'observations.focal', quizz: 'assessments.species',
  scan: 'observations.scan', scans: 'observations.scan', fast_evaluation: 'assessments.rapid', fastEvals: 'assessments.rapid',
  sessions: 'monitoring.enrichment', session: 'monitoring.enrichment', session_evaluation: 'monitoring.enrichment',
  enrichEvals: 'monitoring.enrichmentAnalysis', environmental: 'monitoring.environmentalParameters',
  task: 'logbook.tasks', taskCompleted: 'logbook.tasks', treatment: 'logbook.treatments', training: 'monitoring.training',
});
export const isProductActivityVisible = (scope, type) => !PRODUCT_ACTIVITY_FEATURES[type] || scope.can(PRODUCT_ACTIVITY_FEATURES[type]);
